import React, { Fragment, ReactNode } from "react";

import {
  StyledCode,
  StyledCodeBlock,
  StyledDivider,
  StyledLink,
  StyledList,
  StyledTableBlock,
} from "./Styles";
import {
  isPortableTextMediaImageVariant,
  PortableTextBlockRenderer,
  PortableTextCodeBlock,
  PortableTextDefaultBlockType,
  PortableTextDefaultMark,
  PortableTextHeadingBlock,
  PortableTextHeadingLevel,
  PortableTextLinkMark,
  PortableTextListBlock,
  PortableTextMarkRenderer,
  PortableTextMediaBlock,
  PortableTextSpan,
  PortableTextTableBlock,
  PortableTextTitledTextBlock,
} from "./types";
import { Media } from "../Media";
import { Table, Tbody, Thead } from "../Tables";
import {
  TitledText,
  TitledTextVariant,
  titledTextHeadings,
} from "../TitledText";
import { Body1 } from "../Typography";
import { Typography } from "../Typography/Typography";

/**
 * The default renderers for the universal set — everything a client renders
 * without injection, and nothing more: no product element ever lands here.
 * Injected {@link PortableTextComponents} maps take precedence entry by entry.
 */

export const portableTextDefaultMarkRenderers: Record<
  PortableTextDefaultMark,
  PortableTextMarkRenderer
> = {
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  code: ({ children }) => <StyledCode>{children}</StyledCode>,
  link: ({ mark, children }) => {
    const href = (mark as PortableTextLinkMark | undefined)?.href;

    // A link mark without an href (authored as a plain string) has nowhere
    // to go — keep the text, skip the anchor.
    if (!href) {
      return <>{children}</>;
    }

    return (
      <StyledLink href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </StyledLink>
    );
  },
};

/** The semantic wire levels resolve to typography only here, in the renderer. */
const HEADING_LEVEL_VARIANTS: Record<
  PortableTextHeadingLevel,
  TitledTextVariant
> = {
  1: "title1",
  2: "title2",
  3: "header2",
};

/** The level `heading` and `titled-text` fall back to. */
const DEFAULT_HEADING_LEVEL: PortableTextHeadingLevel = 3;

/**
 * Resolve a semantic wire level to its typography variant. An absent level is
 * the documented default; any other unrecognised level is a serializer bug on
 * a fixed 1–3 scale, so it falls back to the default and is logged, per the
 * contract's degradation rule.
 */
const headingVariantForLevel = (
  level: PortableTextHeadingLevel | undefined
): TitledTextVariant => {
  if (level === undefined) {
    return HEADING_LEVEL_VARIANTS[DEFAULT_HEADING_LEVEL];
  }

  const variant = HEADING_LEVEL_VARIANTS[level];
  if (!variant) {
    console.warn(
      `PortableText: unknown heading level ${level}, falling back to ${DEFAULT_HEADING_LEVEL}.`
    );
    return HEADING_LEVEL_VARIANTS[DEFAULT_HEADING_LEVEL];
  }

  return variant;
};

/** Heading levels map to the same typography TitledText uses. */
const HeadingBlock: PortableTextBlockRenderer = ({ block, children }) => {
  const { level, id } = block as PortableTextHeadingBlock;
  const Heading = titledTextHeadings[headingVariantForLevel(level)];
  return <Heading id={id}>{children}</Heading>;
};

export const portableTextDefaultBlockRenderers: Record<
  PortableTextDefaultBlockType,
  PortableTextBlockRenderer
> = {
  paragraph: ({ children }) => <Body1>{children}</Body1>,
  heading: HeadingBlock,
  list: ({ block, children }) => {
    const ordered = (block as PortableTextListBlock).style === "number";
    return (
      // `data-portable-list` scopes the depth-marker rules in Styles.ts to
      // design-system lists, and marks the ancestors those rules count.
      <StyledList
        as={ordered ? "ol" : "ul"}
        $ordered={ordered}
        data-portable-list=""
      >
        {children}
      </StyledList>
    );
  },
  "list-item": ({ children }) => (
    <Typography variant="Body 1" as="li">
      {children}
    </Typography>
  ),
  media: ({ block }) => {
    const { src, alt, variant, caption } = block as PortableTextMediaBlock;

    // Video (and future) variants carry service semantics, so their renderer
    // is application-catalog territory — an injected `media` override. The
    // universal set covers images only; anything else degrades and warns, per
    // the contract's degradation rule.
    if (variant !== undefined && !isPortableTextMediaImageVariant(variant)) {
      console.warn(
        `PortableText: no default renderer for media variant "${variant}".`
      );
      return null;
    }

    return <Media src={src} alt={alt} variant={variant} caption={caption} />;
  },
  "titled-text": ({ block, children }) => {
    const { title, level } = block as PortableTextTitledTextBlock;
    return (
      <TitledText title={title} variant={headingVariantForLevel(level)}>
        {children}
      </TitledText>
    );
  },
  // No highlighter ships with the default set, so every language — known,
  // unknown or absent — is plain preformatted text; the hint travels on
  // data-language for surfaces that highlight.
  "code-block": ({ block }) => {
    const { code, language } = block as PortableTextCodeBlock;
    return (
      <StyledCodeBlock data-language={language || undefined}>
        <code>{code}</code>
      </StyledCodeBlock>
    );
  },
  table: ({ block, renderSpans }) => {
    const { header, rows } = block as PortableTextTableBlock;
    // Standalone (engine-less) use has no span renderer — keep the raw text.
    const cell = (spans: PortableTextSpan[]): ReactNode =>
      renderSpans
        ? renderSpans(spans)
        : spans.map((span, index) => (
            <Fragment key={index}>{span.text}</Fragment>
          ));

    return (
      <StyledTableBlock>
        <Table>
          <Thead>
            <tr>
              {header.map((spans, index) => (
                <th key={index}>{cell(spans)}</th>
              ))}
            </tr>
          </Thead>
          <Tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((spans, cellIndex) => (
                  <td key={cellIndex}>{cell(spans)}</td>
                ))}
              </tr>
            ))}
          </Tbody>
        </Table>
      </StyledTableBlock>
    );
  },
  divider: () => <StyledDivider />,
};
